import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, History, X } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const OptionBtn = ({ s, testid, onPick }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={() => onPick(s)}
    className="text-left nb-card-sm px-2.5 py-2 bg-white hover:bg-[#EAF5FB] flex items-center justify-between gap-2 text-sm"
  >
    <span className="truncate font-bold">
      {s.name}
      {s.sku && <span className="text-[10px] text-[#607087] font-mono-num ml-1">SKU {s.sku}</span>}
    </span>
    <span className="font-mono-num text-xs shrink-0">{money(s.unit_cost)} / {s.unit}</span>
  </button>
);

const QuickAddForm = ({ original, onCreated, onCancel }) => {
  const [f, setF] = useState({
    name: "",
    sku: "",
    type: original.type || "",
    unit: original.unit || "",
    unit_cost: original.unit_cost ?? "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const { data } = await api.post(`/stock/${original.stock_id}/quick-substitute`, {
        ...f,
        unit_cost: Number(f.unit_cost) || 0,
      });
      toast.success(`${data.name} added to inventory`);
      window.dispatchEvent(new Event("stock-updated"));
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add item");
    } finally {
      setBusy(false);
    }
  };
  const field = (k, label, props = {}) => (
    <label className="min-w-0">
      <div className="label-eyebrow mb-1">{label}</div>
      <input
        data-testid={`sub-quickadd-${k}`}
        className="nb-input py-1.5 text-sm w-full"
        value={f[k]}
        onChange={(e) => set(k, e.target.value)}
        {...props}
      />
    </label>
  );
  return (
    <div data-testid="sub-quickadd-form" className="border-2 border-dashed border-[#0A3A7A] rounded-lg p-2.5 bg-white flex flex-col gap-2">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <div className="col-span-2">{field("name", "Name", { placeholder: "Substitute item name", autoFocus: true })}</div>
        {field("sku", "SKU")}
        {field("type", "Type")}
        {field("unit", "Unit")}
        {field("unit_cost", "Unit cost", { type: "number", step: "0.01", min: "0" })}
      </div>
      <div className="text-[10px] text-[#607087]">
        Supplier, category, departments, material and colour are copied from {original.stock_name}.
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" data-testid="sub-quickadd-cancel" onClick={onCancel} className="nb-btn bg-white px-3 py-1.5 text-xs">Cancel</button>
        <button type="button" data-testid="sub-quickadd-save" onClick={save} disabled={busy} className="nb-btn nb-btn-primary px-3 py-1.5 text-xs">
          {busy ? "Adding…" : "Add & use"}
        </button>
      </div>
    </div>
  );
};

/** v207 — Pick what the supplier actually sent in place of a PO line. */
const SubstitutePicker = ({ original, onPick, onClose }) => {
  const [known, setKnown] = useState([]);
  const [stock, setStock] = useState(null);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.get(`/stock/${original.stock_id}/substitutes`).then(({ data }) => setKnown(data)).catch(() => {});
    api.get("/stock").then(({ data }) => setStock(data)).catch(() => setStock([]));
  }, [original.stock_id]);

  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    const knownIds = new Set(known.map((k) => k.id));
    return (stock || [])
      .filter((s) => s.id !== original.stock_id && !knownIds.has(s.id))
      .filter((s) => `${s.name} ${s.sku || ""}`.toLowerCase().includes(t))
      .slice(0, 8);
  }, [stock, q, known, original.stock_id]);

  return (
    <div data-testid="substitute-picker" className="basis-full border-2 border-[#0A3A7A] rounded-lg p-2.5 bg-[#F6FAFD] flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow text-[#0A3A7A]">What arrived instead of {original.stock_name}?</div>
        <button type="button" data-testid="substitute-picker-close" onClick={onClose} className="nb-btn bg-white p-1" aria-label="close">
          <X size={12} strokeWidth={2.5} />
        </button>
      </div>
      {known.length > 0 && (
        <div className="flex flex-col gap-1" data-testid="substitute-known-list">
          <div className="text-[10px] font-bold text-[#607087] flex items-center gap-1">
            <History size={10} strokeWidth={2.5} /> Previously used substitutes
          </div>
          {known.map((s) => (
            <OptionBtn key={s.id} s={s} testid={`substitute-known-${s.id}`} onPick={onPick} />
          ))}
        </div>
      )}
      {adding ? (
        <QuickAddForm original={original} onCreated={onPick} onCancel={() => setAdding(false)} />
      ) : (
        <>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#607087]" />
            <input
              data-testid="substitute-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={stock ? "Search inventory by name or SKU…" : "Loading inventory…"}
              className="nb-input py-1.5 pl-8 text-sm w-full"
            />
          </div>
          <div className="flex flex-col gap-1 max-h-44 overflow-y-auto">
            {matches.map((s) => (
              <OptionBtn key={s.id} s={s} testid={`substitute-option-${s.id}`} onPick={onPick} />
            ))}
          </div>
          <button
            type="button"
            data-testid="substitute-quickadd-btn"
            onClick={() => setAdding(true)}
            className="nb-btn bg-white px-3 py-1.5 text-xs flex items-center gap-1 self-start"
          >
            <Plus size={12} strokeWidth={2.5} /> Not in inventory? Quick-add new item
          </button>
        </>
      )}
    </div>
  );
};

export default SubstitutePicker;
